import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. Please check your .env.local file.'
  )
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
})

// Database types for TypeScript
export type UserRole = 'patient' | 'doctor' | 'bsohtak_doctor' | 'admin'
export type DoctorType = 'standard' | 'bsohtak_partner' | 'professor' | 'associate_professor'
export type VerificationStatus = 'pending' | 'approved' | 'rejected' | 'suspended'
export type AdminRole = 'super_admin' | 'moderator' | 'support'

export interface User {
  id: string
  email: string
  phone?: string
  full_name?: string
  date_of_birth?: string
  preferred_language?: 'fr' | 'en' | 'ar'
  theme?: 'light' | 'dark' | 'system'
  notifications_enabled?: boolean
  user_role: UserRole
  created_at: string
  updated_at: string
  last_login_at?: string
  onboarding_completed?: boolean
}

export interface Doctor {
  id: string
  user_id: string
  license_number: string
  specialty: string
  sub_specialties?: string[]
  office_address?: string
  city?: string
  country?: string
  district?: string
  latitude?: number
  longitude?: number
  consultation_fee?: number
  currency?: string
  payment_timing?: 'onsite' | 'reservation'
  accepts_insurance?: boolean
  insurance_providers?: string[]
  doctor_type: DoctorType
  verification_status: VerificationStatus
  verified_at?: string
  verified_by?: string
  rejection_reason?: string
  bio?: string
  education?: string[]
  languages?: string[]
  photo_url?: string
  phone?: string
  professional_email?: string
  total_consultations?: number
  rating?: number
  total_reviews?: number
  is_accepting_patients?: boolean
  is_available_online?: boolean
  status?: 'available' | 'busy' | 'offline'
  created_at: string
  updated_at: string
}

export interface Admin {
  id: string
  user_id: string
  can_approve_doctors?: boolean
  can_manage_users?: boolean
  can_view_analytics?: boolean
  can_manage_content?: boolean
  can_manage_subscriptions?: boolean
  role: AdminRole
  assigned_at: string
  assigned_by?: string
  created_at: string
  updated_at: string
}

export interface Appointment {
  id: string
  profile_id: string
  title: string
  type: string
  status: 'scheduled' | 'confirmed' | 'completed' | 'canceled' | 'no_show'
  appointment_date: string
  duration_minutes?: number
  doctor_id?: string
  doctor_name?: string
  doctor_specialty?: string
  location?: string
  location_address?: string
  reason?: string
  user_notes?: string
  created_at: string
  updated_at: string
}
